import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Modal from '@ui/modal/modal';
import GlobalContainer from '@ui/container/global-container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { CreateSpeaker, ESpeakerInviteStatus, Speaker } from '@/types/speakers.types';
import Container from '@ui/container/container';
import UploadLarge from '@ui/uploads/upload-large';
import Text from '@ui/text/text/text';
import createDefaultSpeaker from '@/utils/speakers/create-default-speaker';
import { updateTranslateKey } from '@/utils/update-translation-key';
import useLanguageParams from '@/utils/use-language-params';
import { useAppSelector } from '@/store/hooks';
import {
  selectWorkingProjectDefaultLanguage,
  selectWorkingProjectLanguages,
  selectWorkingSession,
} from '@/store/slices/project/selectors';
import { isValidEmail } from '@/micros/edit-project/grid-areas/main/audiences/utils';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import {
  GetUploadEndpoint,
  getUploadFilename,
  SetItemContentType,
  UploadFileToProvider,
} from '@ui/uploads/validations';
import { isObject } from 'underscore';
import SendInviteModal from './send-invite-modal';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import GetGreenroomUserToken from '@/connection/greenroom/get-greenroom-user-token';
import SendGreenroomInvite from '@/connection/greenroom/send-greenroom-invite';
import { getToken, selectUserChannel } from '@/store/slices/user/selectors';
import { GreenroomUserProfile, GreenroomInviteUser, GreenroomUpdateProfileMask } from '@/types/greenroom/profile.types';
import useLoadWorkingSessionMeetingData from './use-get-working-session-meeting-data';
import TranslateLangsMap from '@/utils/translate-langs-map.json';
import toastTrigger from '@ui/toast/toast-trigger';
import { InsertSpeaker } from '@/connection/speakers/speakers';
import OptionalComponent from '@ui/optional-component/optional-component';
import InitializeGreenroomProfileMask from '@/connection/greenroom/initialize-greenroom-profile-mask';
import UpdateGreenroomProfileMask from '@/connection/greenroom/update-greenroom-profile';
import AttachGreenroomMask from '@/connection/greenroom/attach-greenroom-mask';

type Props = {
  open: boolean;
  initialName: string;
  onClose: () => void;
  handleNewCast: (speaker: Speaker) => void;
};

const InviteCastModal = memo(({ open, initialName, onClose, handleNewCast }: Props) => {
  const token = useAppSelector(getToken);
  const languages = useAppSelector(selectWorkingProjectLanguages);
  const userChannel = useAppSelector(selectUserChannel);
  const session = useAppSelector(selectWorkingSession);

  const [workingSpeaker, setWorkingSpeaker] = useState<CreateSpeaker>(createDefaultSpeaker());
  const [upLoadingImage, setUpLoadingImage] = useState(false);
  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [savingCast, setSavingCast] = useState(false);
  const [invitingCast, setInvitingCast] = useState(false);
  const [greenroomProfile, setGreenroomProfile] = useState<GreenroomUserProfile | undefined>(undefined);

  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const _baseLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
  const baseLanguage = _baseLanguage || 'en';

  const paramsLanguage = useLanguageParams();
  const language = paramsLanguage || baseLanguage;

  const meeting = useLoadWorkingSessionMeetingData({ sessionUuid: session?.uuid });

  const greenroomLanguage = (TranslateLangsMap as Record<string, string>)[meeting?.language || 'en'];

  const onModalTransitionClosed = useCallback(() => {
    setWorkingSpeaker(createDefaultSpeaker());
  }, []);

  const initialFirstNameGuess = initialName?.trim()?.split(' ')?.[0];
  const initialLastNameGuess = initialName?.trim()?.split(' ')?.slice(1).join(' ');
  useEffect(() => {
    setWorkingSpeaker((prev) => ({
      ...prev,
      first_name: initialFirstNameGuess || '',
      last_name: initialLastNameGuess || '',
    }));
  }, [initialFirstNameGuess, initialLastNameGuess, open]);

  const validEmail = useMemo(() => {
    const isValid = isValidEmail(workingSpeaker.email || '');
    const hasGreenroomProfile = !!greenroomProfile;
    const hasContent = workingSpeaker.email?.length || 0 > 0;

    // valid entries for this field are
    // 1. the field has content and it is a valid email
    // 2. the field is empty and there is no greenroomProfile
    return isValid || (!hasGreenroomProfile && !hasContent);
  }, [greenroomProfile, workingSpeaker.email]);

  const validSpeaker = useMemo(() => {
    return (
      workingSpeaker.image &&
      workingSpeaker.first_name.trim() &&
      workingSpeaker.last_name.trim() &&
      workingSpeaker.job_title?.base?.trim() &&
      validEmail // if an invite has been sent, require an email for saving or require the email is valid if not sending greenroom invite
    );
  }, [
    validEmail,
    workingSpeaker.first_name,
    workingSpeaker.image,
    workingSpeaker.job_title?.base,
    workingSpeaker.last_name,
  ]);

  const handleImageFile = useCallback(async (file: File | FileList) => {
    try {
      if (file instanceof File) {
        setUpLoadingImage(true);

        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });

        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );

          SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

          if (finalUrl) {
            setWorkingSpeaker((prev) => ({ ...prev, image: finalUrl, original_image: finalUrl }));
          }
        }
      }
    } catch (error) {
      console.error(error);
      handleImageUploadErrors(error);
    } finally {
      setUpLoadingImage(false);
    }
  }, []);

  const initials = `${workingSpeaker?.first_name?.[0] || ''}${workingSpeaker?.last_name?.[0] || ''}`.trim();

  const handleCloseCastModal = useCallback(() => {
    if (openInviteModal || cropModalOpen) return;

    setGreenroomProfile(undefined);

    onClose();
  }, [cropModalOpen, onClose, openInviteModal]);

  const createSendGreenroomInvitePostBody = (): GreenroomInviteUser | undefined => {
    if (!userChannel || !workingSpeaker.email) return;

    const invitation = {
      email: workingSpeaker.email,
      authentication: 'email_password',
      watcher_only: false,
    };

    return {
      channel: userChannel.channel,
      channel_name: userChannel.name,
      meeting: meeting?.meeting,
      title: `${meeting?.title} - ${greenroomLanguage}`,
      invitations: [invitation],
    };
  };

  const createUpdateGreenroomUserProfileMaskBody = (): GreenroomUpdateProfileMask | undefined => {
    if (!greenroomProfile || !greenroomProfile.gr_user) return;

    const updatedProfile = {
      avatar: workingSpeaker.image,
      email: greenroomProfile.email,
      first_name: workingSpeaker.first_name,
      last_name: workingSpeaker.last_name,
      title: workingSpeaker.job_title.base,
      location: workingSpeaker.location || '',
      bg_image: workingSpeaker.image,
      channel: userChannel?.channel,
    };

    return {
      entityId: '',
      entityType: 'profileMask',
      entityValue: updatedProfile,
    };
  };

  const handleSendGreenroomInvite = async () => {
    if (!workingSpeaker.email || !meeting?.meeting || !token) {
      toastTrigger(
        <div className="S1">
          <div>Unable to invite speaker due to missing parameters:</div>
          {!token ? <div>No token for user.</div> : null}
          {!meeting?.meeting ? <div>Meeting information not found.</div> : null}
          {!workingSpeaker.email ? <div>Email is required to send an invite.</div> : null}
        </div>,
        {
          type: 'error',
          autoClose: 5000,
        }
      );
      return;
    }

    setInvitingCast(true);

    try {
      const greenroomToken = await GetGreenroomUserToken();

      const sendGreenroomInvitePostBody = createSendGreenroomInvitePostBody();

      if (!sendGreenroomInvitePostBody) throw new Error('Invalid user details');
      // create the invite and send it
      const sendInviteResponse = await SendGreenroomInvite(sendGreenroomInvitePostBody, greenroomToken?.token);

      // if the invite was sent, update the workingSpeaker
      setWorkingSpeaker((prev) => ({
        ...prev,
        invite_status: ESpeakerInviteStatus.pending,
      }));

      setGreenroomProfile(sendInviteResponse[0]?.profile);

      // initialize the profile mask
      await InitializeGreenroomProfileMask(meeting?.meeting, workingSpeaker.email, token);
    } catch (e) {
      console.error(e);
      toastTrigger(<div className="S1">There was a problem inviting the cast member</div>, {
        type: 'error',
        autoClose: 5000,
      });
    } finally {
      setInvitingCast(false);
      setOpenInviteModal(false);
    }
  };

  const handleSaveSpeaker = async () => {
    if (!token) return;

    try {
      setSavingCast(true);

      const newSpeaker = await InsertSpeaker(baseLanguage, languages || [baseLanguage], workingSpeaker);

      if (!validEmail && workingSpeaker.email) {
        setWorkingSpeaker((prev) => ({
          ...prev,
          email: null,
        }));
      }

      if (greenroomProfile) {
        if (!workingSpeaker.email) throw new Error('Unable to update profile mask. No email address to update.');

        // if we have a greenroom profile, an invite was sent to the user. We need to update the greenroom profile with the rest of the
        const updateGreenroomUserProfilePostBody = createUpdateGreenroomUserProfileMaskBody();

        if (updateGreenroomUserProfilePostBody && meeting?.meeting) {
          //update the profile mask with the rest of the speaker details
          const updatedGreenroomProfileMask = await UpdateGreenroomProfileMask(
            meeting?.meeting,
            updateGreenroomUserProfilePostBody,
            token
          );
          const maskId = updatedGreenroomProfileMask?.entityValue?.id;

          if (!maskId) throw new Error('Failed to update profile mask. No maskId');

          // attach the mask to the invited user email
          await AttachGreenroomMask(meeting.meeting, maskId, workingSpeaker.email, token);

          setGreenroomProfile(undefined);
        }
      }

      handleNewCast(newSpeaker);
    } catch (e) {
      console.error(e);
      toastTrigger(<div className="S1">There was a problem saving the cast member</div>, {
        type: 'error',
        autoClose: 5000,
      });
    } finally {
      setSavingCast(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onRequestClose={handleCloseCastModal}
        onTransitionClosed={onModalTransitionClosed}
        customSize={{
          minHeight: '200px',
          maxHeight: '472px',
          height: '472px',
          width: '640px',
          maxWidth: 'calc(100vw - 40px)',
          minWidth: '256px',
        }}
        renderFooterContent={() => (
          <Container
            borderRadius="0 0 24px 24px"
            design="GlobalCont1"
            className="flex ai-center jc-flex-end"
            width="100%"
            height="80px"
            padding="16px 32px 24px 16px"
          >
            <div className="flex ai-center gap-8">
              <Button design="LocalBttnSec" designSize="medium" onClick={handleCloseCastModal}>
                <Text className="L1">Cancel</Text>
              </Button>
              <Button
                design="LocalBttnPrim"
                designSize="medium"
                disabled={!validSpeaker || savingCast || invitingCast}
                onClick={handleSaveSpeaker}
              >
                <Text className="L1">Save</Text>
              </Button>
            </div>
          </Container>
        )}
        footerClassName="width-100-percent"
        contentClassName="min-width-fit-content radius-t-l-24 radius-t-r-24"
        contentWrapperClassName="max-width-100-percent overflow-auto"
        backgroundColor="inherit"
      >
        <Container
          borderRadius="24px 24px 0 0"
          minHeight="100%"
          className="flex fw-wrap gap-24 flex-column-mobile"
          padding="32px 32px 0 32px"
          design="GlobalCont1"
          width="100%"
        >
          <Container height="fit-content" maxWidth="200px" width="100%" className="flex-1 flex jc-center">
            <Container height="200px" width="200px" className="flex ai-center jc-center">
              <UploadLarge
                editable
                onCropModalToggle={setCropModalOpen}
                ref={uploadInputRef}
                EditActionsPreHoverContent={() => (
                  <Container
                    onClick={() => uploadInputRef?.current?.click()}
                    className="position-absolute top-0 left-0 cursor-pointer"
                    height="200px"
                    width="200px"
                    style={{
                      transform: 'translate(calc(-100% + 11px), -11px)',
                    }}
                  />
                )}
                uploading={upLoadingImage}
                style={{
                  height: '200px',
                  minHeight: '200px',
                  maxHeight: '200px',
                  width: '200px',
                  minWidth: '200px',
                  maxWidth: '200px',
                  background: 'var(--secondary-violet-700)',
                  border: '1px dashed var(--gray-600)',
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                }}
                onDelete={() => setWorkingSpeaker((prev) => ({ ...prev, image: '' }))}
                displayImageUrl={workingSpeaker.image}
                hidePlaceholderImage
                hideIconIfImage
                standard
                onFile={handleImageFile}
                allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
                CustomTitle={
                  <Container width="100%" height="100%" className="flex-column ai-center">
                    <Container width="100%" className="flex-1 flex ai-flex-end jc-center margin-b-12">
                      <Text tag="h1" className="H1" opacity={initials ? 1 : 0.2}>
                        {initials || 'AB'}
                      </Text>
                    </Container>

                    <Container
                      className="square-40 radius-full flex ai-center jc-center"
                      margin="auto auto 16px auto"
                      background={'var(--local-bttn-sec-transp-hover-bg)'}
                    >
                      <Icon name={ICONS.IMAGE_MEDIA_ADD} />
                    </Container>
                  </Container>
                }
              />
            </Container>
          </Container>

          <Container width="100%" maxWidth="calc(640px - 200px)" className="flex-1 flex-column gap-8">
            <Container
              background="var(--local-chip-default-bg)"
              border="2px solid var(--local-chip-default-outline)"
              borderRadius="100px"
              padding="8px 16px"
              height="32px"
              width="fit-content"
              className="flex pi-center"
            >
              <Text className="S3" design="GlobalIcoText1">
                Speaker
              </Text>
            </Container>

            <div className="flex ai-center gap-16 jc-space-evenly flex-column-small">
              <label htmlFor="firstName" className="flex-1 flex-column gap-4 width-100-percent">
                <Text design="GlobalIcoText3" className="P2 margin-l-16">
                  First name{' '}
                  <Text tag="span" design="GlobalIcoText2">
                    *
                  </Text>
                </Text>
                <GlobalContainer<'input'>
                  id="firstName"
                  TagName="input"
                  type="text"
                  value={workingSpeaker.first_name}
                  onChange={(e) => setWorkingSpeaker((prev) => ({ ...prev, first_name: e.target.value }))}
                  css="background-color: transparent; width: 100%; padding: 10px 16px; outline: none; border: 1px solid var(--global-ico-text-3); border-radius: 100px;"
                  autoFocus
                  className="P2"
                  mode="GlobalIcoText1"
                  placeholder="Add name"
                />
              </label>

              <label htmlFor="lastName" className="flex-1 flex-column gap-4 width-100-percent">
                <Text design="GlobalIcoText3" className="P2 margin-l-16">
                  Last name{' '}
                  <Text tag="span" design="GlobalIcoText2">
                    *
                  </Text>
                </Text>
                <GlobalContainer<'input'>
                  id="lastName"
                  TagName="input"
                  type="text"
                  value={workingSpeaker.last_name}
                  onChange={(e) => setWorkingSpeaker((prev) => ({ ...prev, last_name: e.target.value }))}
                  css="background-color: transparent; width: 100%; padding: 10px 16px; outline: none; border: 1px solid var(--global-ico-text-3); border-radius: 100px;"
                  className="P2"
                  mode="GlobalIcoText1"
                  placeholder="Add name"
                />
              </label>
            </div>

            <div className="flex ai-center gap-16 jc-space-evenly flex-column-small">
              <label htmlFor="title" className="flex-1 flex-column gap-4 width-100-percent">
                <Text design="GlobalIcoText3" className="P2 margin-l-16">
                  Title{' '}
                  <Text tag="span" design="GlobalIcoText2">
                    *
                  </Text>
                </Text>
                <GlobalContainer<'input'>
                  id="title"
                  TagName="input"
                  type="text"
                  value={workingSpeaker.job_title?.base}
                  onChange={(e) =>
                    setWorkingSpeaker((prev) => ({
                      ...prev,
                      job_title: updateTranslateKey({
                        translateString: workingSpeaker.job_title ?? { base: '', changed: '' },
                        input: e.target.value,
                        baseLanguage,
                        language,
                      }),
                    }))
                  }
                  css="background-color: transparent; width: 100%; padding: 10px 16px; outline: none; border: 1px solid var(--global-ico-text-3); border-radius: 100px;"
                  className="P2"
                  mode="GlobalIcoText1"
                  placeholder="Add title"
                />
              </label>

              <label htmlFor="location" className="flex-1 flex-column gap-4 width-100-percent">
                <Text design="GlobalIcoText3" className="P2 margin-l-16">
                  Location
                </Text>
                <GlobalContainer<'input'>
                  id="location"
                  TagName="input"
                  type="text"
                  value={workingSpeaker?.location || ''}
                  onChange={(e) => setWorkingSpeaker((prev) => ({ ...prev, location: e.target.value }))}
                  css="background-color: transparent; width: 100%; padding: 10px 16px; outline: none; border: 1px solid var(--global-ico-text-3); border-radius: 100px;"
                  className="P2"
                  mode="GlobalIcoText1"
                  placeholder="Add location"
                />
              </label>
            </div>

            <label htmlFor="email" className="flex-column gap-4">
              <Text design="GlobalIcoText3" className="P2 margin-l-16 flex ai-center gap-4">
                Email{' '}
                <OptionalComponent display={!!greenroomProfile}>
                  <Text tag="span" design="GlobalIcoText2">
                    *
                  </Text>
                </OptionalComponent>
                <TooltipPortal
                  styles={{ padding: '8px' }}
                  tooltipContent={
                    <Text color="var(--primitives-mode-1-gray-100)" className="P2">
                      Required if they'll be joining as a remote presenter in Greenroom
                    </Text>
                  }
                  childrenWrapperClass="flex"
                >
                  <Icon name={ICONS.INFO_CIRCLE} size={10} />
                </TooltipPortal>
              </Text>
              <div style={{ position: 'relative' }}>
                <GlobalContainer<'input'>
                  id="email"
                  TagName="input"
                  type="email"
                  disabled={!!greenroomProfile}
                  value={workingSpeaker?.email || ''}
                  onChange={(e) => setWorkingSpeaker((prev) => ({ ...prev, email: e.target.value }))}
                  css={`
                    background-color: transparent;
                    width: 100%;
                    padding: 10px calc(${validEmail ? '97px' : '52px'} + 8px + 16px) 10px 16px;
                    outline: none;
                    border: 1px solid var(--global-ico-text-3);
                    border-radius: 100px;
                  `}
                  className="P2"
                  mode="GlobalIcoText1"
                  placeholder="Add email"
                />
                <Button
                  clear={!validEmail}
                  disabled={
                    !validEmail || savingCast || invitingCast || !!greenroomProfile || !workingSpeaker?.email?.length
                  }
                  design={validEmail && workingSpeaker?.email?.length ? 'LocalBttnSecHalfTransp' : undefined}
                  borderRadius="100px"
                  position="absolute"
                  className={`flex pi-center top-4 right-4 ${validEmail ? '' : 'pointer-events-none'}`}
                  height="calc(100% - 8px)"
                  width={`calc(${validEmail ? '97px' : '52px'} + 8px + 16px)`}
                  onClick={() => setOpenInviteModal(true)}
                >
                  <Text
                    whiteSpace="nowrap"
                    design={validEmail ? undefined : 'GlobalIcoText3'}
                    width="100%"
                    className={`${validEmail ? 'L2' : 'P2'} text-center`}
                  >
                    {validEmail && workingSpeaker?.email?.length
                      ? greenroomProfile
                        ? 'Invite sent'
                        : 'Send invite'
                      : 'optional'}
                  </Text>
                </Button>
              </div>
            </label>

            <label htmlFor="bio" className="flex-column gap-4">
              <Text design="GlobalIcoText3" className="P2 margin-l-16">
                Bio
              </Text>
              <GlobalContainer<'textarea'>
                id="email"
                rows={4}
                TagName="textarea"
                value={workingSpeaker?.description?.base || ''}
                onChange={(e) =>
                  setWorkingSpeaker((prev) => ({
                    ...prev,
                    description: updateTranslateKey({
                      translateString: workingSpeaker.description ?? { base: '', changed: '' },
                      input: e.target.value,
                      baseLanguage,
                      language,
                    }),
                  }))
                }
                css="background-color: transparent; width: 100%; padding: 10px 16px; outline: none; border: 1px solid var(--global-ico-text-3); border-radius: 16px;"
                className="P2 clear-textarea"
                mode="GlobalIcoText1"
                placeholder="Add bio"
              />
            </label>
          </Container>
        </Container>
      </Modal>

      <SendInviteModal
        open={openInviteModal}
        email={workingSpeaker.email || ''}
        onClose={() => setOpenInviteModal(false)}
        handleSendGreenroomInvite={handleSendGreenroomInvite}
        sendingInvite={invitingCast}
      />
    </>
  );
});

InviteCastModal.displayName = 'InviteCastModal';

export default InviteCastModal;
